import { flat } from "@suse-o11y/flat";
import { groupBy } from "@suse-o11y/group-by";
import { IconDragHandle } from "@suse-o11y/icons";
import { map } from "@suse-o11y/map";
import { pickBy } from "@suse-o11y/pick-by";
import { pipe } from "@suse-o11y/pipe";
import { prop } from "@suse-o11y/prop";
import { refId } from "@suse-o11y/ref-id";
import { reorder } from "@suse-o11y/reorder";
import { useComputed } from "@suse-o11y/signal";
import { splice } from "@suse-o11y/splice";
import { toValues } from "@suse-o11y/to-values";
import React from "react";

import { DragAndDrop } from "~/components/drag-and-drop";
import { Draggable } from "~/components/drag-and-drop/draggable";
import { DraggablePlaceholder } from "~/components/drag-and-drop/draggable-placeholder";
import { Droppable } from "~/components/drag-and-drop/droppable";
import { DragHandle } from "~/components/drag-and-drop/handle";
import { FormField } from "~/components/form/field";
import { FormLabel } from "~/components/form/label";
import { FormFieldReset } from "~/components/form/reset";
import { Tooltip } from "~/components/tooltip";
import { TooltipContent } from "~/components/tooltip/content";
import { TooltipTrigger } from "~/components/tooltip/trigger";

import type { FormState } from "./_form-state";
import { StaticValue } from "./_static-value";

function useValidateDuplicates(state: FormState): void {
	useComputed(
		(monitor) => {
			// reset all errors
			state.setError(null);

			const values = state.getElements(monitor);

			const duplicates = pipe(
				values,
				map(({ fields }) => ({
					/**
					 * What is visible at the end is the label with a fallback to value,
					 * so we assign the error to the label field as it is responsible for the final display.
					 */
					field: fields.label,
					label: fields.label.getInput(monitor).trim() || fields.value.getInput(monitor).trim(),
				})),
				groupBy(prop("label")),
				pickBy((list) => list.length > 1),
				toValues(),
				flat(),
			);

			for (const { field } of duplicates) {
				field.setError(["Duplicated value label"]);
			}
		},
		[state],
	);
}

export interface VariableFormProps {
	canReorder: boolean;
	state: FormState;
}

export const VariableForm: React.FC<VariableFormProps> = ({ canReorder, state }) => {
	useValidateDuplicates(state);

	return (
		<FormField className="items-stretch" state={state}>
			<FormLabel asChild>
				<div>
					<FormLabel>Values</FormLabel>
					<FormFieldReset />
				</div>
			</FormLabel>

			{(monitor, field) => (
				<DragAndDrop
					onDragEnd={({ source, destination }) => {
						if (destination) {
							state.setElements((values) => reorder(values, source.index, destination.index));
						}
					}}
				>
					<Droppable className="-mt-2">
						{field.getElements(monitor).map((value, index, arr) => {
							const isDragEnabled = canReorder && arr.length > 1;

							return (
								<Draggable asChild index={index} isDragDisabled={!isDragEnabled} key={refId(value)}>
									<StaticValue
										className="mt-2"
										dragHandle={
											isDragEnabled && (
												<Tooltip disableHoverableContent>
													<TooltipTrigger asChild>
														<DragHandle className="button button-square button-sm">
															<IconDragHandle className="text-grey-500" />
														</DragHandle>
													</TooltipTrigger>

													<TooltipContent>Drag to reorder</TooltipContent>
												</Tooltip>
											)
										}
										onDelete={
											arr.length > 1
												? () => {
														state.setInput(splice(index, 1, []));
													}
												: undefined
										}
										state={value}
									/>
								</Draggable>
							);
						})}

						<DraggablePlaceholder />
					</Droppable>
				</DragAndDrop>
			)}

			<button
				className="button button-primary mt-4 uppercase"
				onClick={() => {
					state.setInput((current) => [...current, { value: "", label: "" }]);
				}}
				type="button"
			>
				Add value
			</button>
		</FormField>
	);
};
